import { FC, Fragment, ReactNode } from 'react';
import CompanyMenuItem from './components/CompanyMenuItem';
import MenuItem from './components/MenuItem';
import FavouritesSection from './components/FavouritesSection';
import { MenuGroupInterface, MenuItemInterface } from './types';
import { useStore } from '../../../../hooks/useStore';
import {
  companyInfo,
  getFlightDeckNavList
} from './components/BusinessNavLists/FlightDeckNavList';
import useContactTypes from '@hooks/api/useContactTypes';
import { navLinkToTestSegment, navTitleToTestSegment } from './navTestId';
import { Text } from 'stubben-edge-unified-frontend-react-components';
import { usePipelineMyWorkstreamsQuery } from '@/api-client/pipeline/use-pipeline-queries';
import { useSidebarCollapsed } from '../Sidebar/SidebarCollapsedContext';
import { canShowNavItem } from './canShowNavItem';

const Menu: FC = () => {
  const { collapsed } = useSidebarCollapsed();
  const { authStore } = useStore();
  const { hasPermission, hasFeature, user } = authStore;

  const canViewMyWorkstreams = hasPermission([
    'pipeline_view',
    'pipeline_access',
    'pipeline_manage',
    'task_view',
    'task_manage'
  ]);

  const {
    data: myWorkstreams,
    isLoading: isLoadingMyWorkstreams,
    isFetching: isFetchingMyWorkstreams
  } = usePipelineMyWorkstreamsQuery({
    enabled: canViewMyWorkstreams
  });

  const { data: companyTypes } = useContactTypes('company');
  const { data: individualTypes } = useContactTypes('individual');

  const FlightDeckNavList = getFlightDeckNavList(
    authStore,
    companyTypes,
    individualTypes,
    {
      myWorkstreams: myWorkstreams ?? [],
      isLoadingMyWorkstreams:
        canViewMyWorkstreams &&
        (isLoadingMyWorkstreams ||
          (isFetchingMyWorkstreams && myWorkstreams == null))
    }
  );

  const isSEG = user?.company?.tenant_is_seg_parent || false;

  const showFlightplan = (title: ReactNode) => {
    return !(isSEG && title === 'Flightplan');
  };

  return (
    <>
      {companyInfo && <CompanyMenuItem {...companyInfo} />}
      <FavouritesSection navGroups={FlightDeckNavList} />
      {FlightDeckNavList.map(
        (group: MenuGroupInterface, index: number) => {
          const sectionSegment = group.title
            ? navTitleToTestSegment(group.title)
            : group.items?.[0]?.link
              ? navLinkToTestSegment(group.items[0].link)
              : `group-${index}`;

          return (
          (!group.rules || hasPermission(group.rules)) && (
            <Fragment key={index}>
              {group.title && !collapsed && (
                <div
                  className="px-6 py-4"
                  data-testid={`sidebar-nav-section-${navTitleToTestSegment(
                    group.title
                  )}`}
                >
                  <Text
                    size="sm"
                    bold="heavy"
                    variant="secondary"
                    testIdentifier={`sidebar-nav-section-label-${navTitleToTestSegment(
                      group.title
                    )}`}
                  >
                    {group.title}
                  </Text>
                </div>
              )}
              {group.items?.map(
                (item: MenuItemInterface, index) =>
                  // this will allow you to not render an item in the list on a certain page
                  canShowNavItem(item, hasPermission, hasFeature) &&
                  showFlightplan(item?.title) && (
                    // this will allow you to render an item in the list on a certain page
                    <MenuItem {...item} key={index} />
                  )
              )}
              {index !== FlightDeckNavList.length - 1 && !collapsed && (
                <div
                  className="px-6 py-4"
                  data-testid={`sidebar-nav-section-divider-${sectionSegment}`}
                >
                  <div className="border-b border-gray-200"></div>
                </div>
              )}
            </Fragment>
          )
        );
        }
      )}
    </>
  );
};

export default Menu;
